/**
 * Evidence-tier metadata used by badges, filters, and the detail drawer.
 * Colours are driven by the Tailwind `evidence.*` palette so badge styles stay
 * consistent with the design tokens.
 */

import type { EvidenceTier } from "@shared/schema";

export interface EvidenceTierMeta {
  id: EvidenceTier;
  label: string;
  shortLabel: string;
  color: string; // css var/hex for inline usage
  description: string;
  caveat?: string;
}

export const EVIDENCE_TIER_META: Record<EvidenceTier, EvidenceTierMeta> = {
  experimental: {
    id: "experimental",
    label: "Experimental",
    shortLabel: "Exp",
    color: "hsl(152 62% 40%)",
    description: "Direct experimental evidence — Y2H, co-IP, pull-down, BiFC, PLA, etc.",
  },
  curated_db: {
    id: "curated_db",
    label: "Curated DB",
    shortLabel: "DB",
    color: "hsl(215 80% 55%)",
    description: "Manually curated in a reference database (BioGRID, IntAct, MINT, etc.).",
  },
  ai_structural_high: {
    id: "ai_structural_high",
    label: "AI structural (high)",
    shortLabel: "AIᴴ",
    color: "hsl(268 70% 58%)",
    description: "AlphaFold-Multimer / equivalent with high pLDDT/ipTM.",
    caveat: "Structural hypothesis — requires orthogonal experimental confirmation.",
  },
  ai_structural_low: {
    id: "ai_structural_low",
    label: "AI structural (low)",
    shortLabel: "AIᴸ",
    color: "hsl(288 55% 62%)",
    description: "AI structural prediction with lower confidence — treat as hypothesis.",
    caveat: "Structural hypothesis — requires orthogonal experimental confirmation.",
  },
  cross_species_inferred: {
    id: "cross_species_inferred",
    label: "Cross-species",
    shortLabel: "xSp",
    color: "hsl(178 58% 44%)",
    description: "Inferred from orthologous proteins in another organism.",
  },
  textmining: {
    id: "textmining",
    label: "Text mining",
    shortLabel: "TM",
    color: "hsl(44 90% 52%)",
    description: "Co-mentioned in literature; association may be indirect.",
  },
  coexpression: {
    id: "coexpression",
    label: "Co-expression",
    shortLabel: "CoEx",
    color: "hsl(26 90% 56%)",
    description: "Correlated expression; does not imply direct physical interaction.",
  },
  neighborhood: {
    id: "neighborhood",
    label: "Neighborhood",
    shortLabel: "Nhb",
    color: "hsl(220 8% 60%)",
    description: "Genomic co-localization; weak evidence for direct interaction.",
  },
  weak_conflicting: {
    id: "weak_conflicting",
    label: "Weak / conflicting",
    shortLabel: "Wk",
    color: "hsl(0 72% 55%)",
    description: "Conflicting reports or very low combined confidence.",
  },
};

export const EVIDENCE_TIER_ORDER: EvidenceTier[] = [
  "experimental",
  "curated_db",
  "ai_structural_high",
  "ai_structural_low",
  "cross_species_inferred",
  "textmining",
  "coexpression",
  "neighborhood",
  "weak_conflicting",
];
